'use client';

import {
  Checkbox,
  Skeleton,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableBody as MuiTableBody,
  Table as MuiTable,
  TableContainer,
  Divider,
} from '@mui/material';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  Cell,
  Column,
  ColumnDef,
  PaginationState,
  Row,
  SortingState,
  TableOptions,
  Table as TableType,
} from '@tanstack/react-table';
import classnames from 'classnames';
import React, {
  Fragment,
  useMemo,
  useState,
  useEffect,
  type CSSProperties,
} from 'react';

import { useWorkspace } from '@/contexts/useWorkspace';

import { FiltersBar } from './filtersBar';
import tableStyles from './table.module.css';

import PaginationBar from '../shared/paginationBar';

interface RowData<T> {
  data: T[];
  showFiltersBar: boolean;
  isLoading: boolean;
  isMultiSelect: boolean;
  MultiSelectActionButton?: (props: { entities: T[] }) => React.ReactNode;
  disableSorting?: boolean;
  sorting?: SortingState;
  columns: ColumnDef<T>[];
  hiddenColumns?: string[];
  disableSelection?: boolean;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    recordsCount?: number;
    recordsLimit?: number;
    onChange: (pagination: PaginationState) => void;
    reachedLastPage?: boolean;
  };
  paginationBarActions?: ((props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: Column<any>[];
  }) => React.ReactElement)[];
  onSortingChanged?: (state: SortingState) => void;
  testId?: string;
  entityLabel: string;
  pinnedColumns?: string[];
  stickyHeader?: boolean;
  onTableLoaded?: (table: TableType<T>) => void;
  onRowContentChange?: () => Promise<void>;
}

const getSelectionColumn = <T,>(
  enabledCheckboxes: Record<string, boolean>
): ColumnDef<T> => ({
  id: 'select',
  enableHiding: false,
  size: 53,
  meta: { isCheckbox: true },
  cell: ({ row }) => {
    const isSelected = enabledCheckboxes[row.index];
    return (
      <Checkbox
        checked={isSelected}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected()}
      />
    );
  },
});

export const Table = <T extends object>({
  data,
  showFiltersBar = false,
  hiddenColumns,
  isMultiSelect,
  MultiSelectActionButton,
  columns,
  sorting,
  isLoading,
  pagination,
  disableSorting,
  onSortingChanged,
  testId,
  entityLabel,
  pinnedColumns,
  stickyHeader,
  paginationBarActions,
  onTableLoaded,
  onRowContentChange,
}: RowData<T>) => {
  const { rightPanel } = useWorkspace();
  const [enabledCheckboxes, setEnabledCheckboxes] = useState<
    Record<string, boolean>
  >({});

  const [isTableScrolledHorizontally, setTableScrolledHorizontally] =
    useState(false);
  const [isTableScrolledVertically, setTableScrolledVertically] =
    useState(false);

  // local storage to store column visibility key
  const storageKey = `table-${entityLabel}-columnVisibility`;

  const persistColumnVisibility = false;
  // typeof window !== 'undefined' && !isDemo && !isDev;
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    // On first mount, read from localStorage:
    if (persistColumnVisibility) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    }
    // If nothing stored, build from hiddenColumns prop if provided
    if (hiddenColumns?.length) {
      return hiddenColumns.reduce<Record<string, boolean>>((agg, colId) => {
        agg[colId] = false; // false means hidden in TanStack's terminology
        return agg;
      }, {});
    }
    return {};
  });

  // Whenever columnVisibility changes, store it in localStorage
  useEffect(() => {
    if (persistColumnVisibility) {
      localStorage.setItem(storageKey, JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, storageKey, persistColumnVisibility]);

  const extendedColumns = useMemo(
    () => [
      ...(isMultiSelect ? [getSelectionColumn<T>(enabledCheckboxes)] : []),
      ...columns,
    ],
    [isMultiSelect, columns, enabledCheckboxes]
  );

  const tableOptions: TableOptions<T> = {
    data,
    columns: extendedColumns,
    enableRowSelection: true,
    enableSortingRemoval: false,
    enableMultiRowSelection: isMultiSelect,
    initialState: {
      columnPinning: {
        left: [
          ...(pinnedColumns?.length && isMultiSelect ? ['select'] : []),
          ...(pinnedColumns || []),
        ],
        right: [],
      },
    },
    state: {
      sorting,
      columnVisibility,
    },

    // Called whenever user toggles a column's visibility
    onColumnVisibilityChange: setColumnVisibility,

    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    onSortingChange: (state) => {
      if (!disableSorting && sorting && onSortingChanged) {
        const newState = typeof state === 'function' ? state(sorting) : state;
        onSortingChanged(newState);
      }
    },
  };

  const table = useReactTable(tableOptions);

  const getIsAllRecordsSelected = () => {
    return (
      !isLoading &&
      table.getRowModel().rows.every((row) => enabledCheckboxes[row.index])
    );
  };
  const getIsSomeRecordsSelected = () => {
    return (
      table.getRowModel().rows.some((row) => enabledCheckboxes[row.index]) &&
      !getIsAllRecordsSelected()
    );
  };
  const multiSelect = isMultiSelect
    ? {
        getIsAllRecordsSelected,
        getIsSomeRecordsSelected,
        getToggleAllRecordsSelectedHandler: () => (event: unknown) => {
          (event as React.MouseEvent).stopPropagation();
          rightPanel.setIsOpen(false);
          setEnabledCheckboxes((state) =>
            table
              .getRowModel()
              .rows.reduce<Record<string, boolean>>((acc, row) => {
                acc[row.index] = getIsSomeRecordsSelected()
                  ? true
                  : !state[row.index];
                return acc;
              }, {})
          );
        },
        ActionButton: MultiSelectActionButton ? (
          <MultiSelectActionButton
            entities={getCheckedEntities(table, enabledCheckboxes)}
          />
        ) : undefined,
      }
    : undefined;

  const onPageChange = (page: number) => {
    pagination?.onChange({
      pageSize: pagination.pageSize,
      pageIndex: page,
    });
  };

  const onRowsPerPageChange = (pageIndex: number, pageSize: number) => {
    pagination?.onChange({
      pageSize,
      pageIndex,
    });
  };

  useEffect(() => {
    onTableLoaded?.(table);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCellClick = (
    e: React.MouseEvent,
    row: Row<T>,
    column: Column<T>
  ) => {
    const meta = column.columnDef.meta as
      | { isCheckbox?: boolean; isSelectable?: boolean }
      | undefined;
    const checkboxClicked = meta?.isCheckbox;
    const isClickOnCellItself = e.target === e.currentTarget;
    const customCellClicked = !isClickOnCellItself && meta?.isSelectable;
    const isRowSelected = row.getIsSelected();
    const isCheckboxEnabled = enabledCheckboxes[row.index];

    const removeAllSelections = () => {
      table.resetRowSelection();
    };

    if (customCellClicked) {
      setEnabledCheckboxes({});
      if (isCheckboxEnabled || !isRowSelected) {
        table.setRowSelection({ [row.index]: true });
      } else {
        removeAllSelections();
      }
    } else if (checkboxClicked) {
      rightPanel.setIsOpen(false);
      removeAllSelections();
      setEnabledCheckboxes((state) => ({
        ...state,
        [row.index]: !isCheckboxEnabled,
      }));
    }
  };

  return (
    <>
      {showFiltersBar && <FiltersBar columns={table.getAllColumns()} />}

      {(isMultiSelect || pagination || paginationBarActions?.length) && (
        <>
          <Divider />
          <PaginationBar
            pagination={pagination}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            entityLabel={entityLabel}
            selection={{
              multiSelect,
              selectedRecordsCount: Object.values(enabledCheckboxes).filter(
                (value) => value
              ).length,
            }}
            isLoading={isLoading}
            actions={paginationBarActions?.map((Component) => (
              <Component key={Component.name} columns={table.getAllColumns()} />
            ))}
          />
        </>
      )}

      <TableContainer
        className='flex-grow min-h-[500px]'
        onScroll={(event) => {
          const target = event.target as HTMLDivElement;
          if (pinnedColumns?.length && typeof target.scrollLeft === 'number') {
            const isScrolled = target.scrollLeft > 0;
            if (isScrolled !== isTableScrolledHorizontally) {
              setTableScrolledHorizontally(isScrolled);
            }
          }
          if (typeof target.scrollTop === 'number') {
            const isScrolled = target.scrollTop > 0;
            if (isScrolled !== isTableScrolledVertically) {
              setTableScrolledVertically(isScrolled);
            }
          }
        }}
      >
        <MuiTable
          className={tableStyles.table}
          data-testid={testId}
          stickyHeader={stickyHeader}
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={getCommonPinningStyles(
                      header.column,
                      isTableScrolledHorizontally
                    )}
                    className={classnames({
                      'cursor-pointer select-none':
                        !disableSorting && header.column.getCanSort(),
                      [tableStyles.sorted]: header.column.getIsSorted(),
                      [tableStyles.pinned]:
                        !isLoading && header.column.getIsPinned() === 'left',
                      [tableStyles.scrollingVertically]:
                        isTableScrolledVertically,
                    })}
                    onClick={() => {
                      if (!disableSorting && header.column.getCanSort()) {
                        header.column.toggleSorting();
                      }
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div className='whitespace-pre-line justify-between flex items-center min-h-[20px]'>
                        <Typography variant='overline' className='w-full'>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Typography>
                        {header.column.getCanSort() && (
                          <i
                            style={{
                              color: 'var(--mui-palette-text-gray1)',
                            }}
                            className={classnames('w-[20px] h-[20px]', {
                              hidden: !header.column.getIsSorted(),
                              'ri-arrow-down-s-line':
                                header.column.getIsSorted() === 'asc',
                              'ri-arrow-up-s-line':
                                header.column.getIsSorted() === 'desc',
                            })}
                          />
                        )}
                      </div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody<T>
            isLoading={isLoading}
            isMultiSelect={isMultiSelect}
            table={table}
            isTableScrolledHorizontally={isTableScrolledHorizontally}
            onCellClick={handleCellClick}
            isRowSelected={(row) =>
              enabledCheckboxes[row.index] || row.getIsSelected()
            }
            onRowContentChange={onRowContentChange}
          />
        </MuiTable>
      </TableContainer>
    </>
  );
};

const LoadingState = ({
  numOfRows,
  numOfColumns,
}: {
  numOfRows: number;
  numOfColumns: number;
}) => {
  return (
    <MuiTableBody>
      {[...Array(numOfRows)].map((_, i) => (
        <TableRow key={i}>
          {[...Array(numOfColumns)].map((_, j) => (
            <TableCell key={j} className='text-center'>
              <Skeleton variant='text' />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </MuiTableBody>
  );
};

const NoDataState = ({ columnCount }: { columnCount: number }) => {
  return (
    <MuiTableBody>
      <TableRow>
        <TableCell colSpan={columnCount} className='text-center'>
          No data available
        </TableCell>
      </TableRow>
    </MuiTableBody>
  );
};

function TableBody<T>({
  table,
  isLoading,
  isTableScrolledHorizontally,
  isMultiSelect,
  onCellClick,
  isRowSelected,
  onRowContentChange,
}: {
  table: TableType<T>;
  isLoading: boolean;
  getDynamicColProps?: (cell: Cell<T, unknown>) => Record<string, unknown>;
  isTableScrolledHorizontally: boolean;
  isMultiSelect: boolean;
  onCellClick: (e: React.MouseEvent, row: Row<T>, column: Column<T>) => void;
  isRowSelected: (row: Row<T>) => boolean;
  onRowContentChange?: () => Promise<void>;
}) {
  if (isLoading) {
    return (
      <LoadingState
        numOfRows={10}
        numOfColumns={table.getVisibleFlatColumns().length}
      />
    );
  }

  if (!isLoading && table.getRowModel().rows.length === 0) {
    return <NoDataState columnCount={table.getVisibleFlatColumns().length} />;
  }

  if (table.getRowModel().rows.length > 0) {
    return (
      <MuiTableBody
        className={`${classnames({
          'opacity-30': isLoading,
        })} align-top`}
      >
        {table.getRowModel().rows.map((row) => {
          return (
            <Fragment key={row.id}>
              <TableRow
                data-selected={isRowSelected(row)}
                className={classnames({
                  [tableStyles.selected]: isRowSelected(row),
                })}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as
                    | { isCheckbox?: boolean; isSelectable?: boolean }
                    | undefined;
                  return (
                    <TableCell
                      key={cell.id}
                      sx={getCommonPinningStyles(
                        cell.column,
                        isTableScrolledHorizontally
                      )}
                      className={classnames('cursor-default', {
                        [tableStyles.pinned]:
                          cell.column.getIsPinned() === 'left',
                        [tableStyles.multi]: isMultiSelect,
                        'cursor-pointer': meta?.isCheckbox,
                      })}
                      onClick={(e) => {
                        onCellClick(e, row, cell.column);
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, {
                        ...cell.getContext(),
                        onRowContentChange,
                      })}
                    </TableCell>
                  );
                })}
              </TableRow>
            </Fragment>
          );
        })}
      </MuiTableBody>
    );
  }
  return null;
}
function getCheckedEntities<T>(
  table: TableType<T>,
  enabledCheckboxes: Record<string, boolean>
) {
  const selectedEntities = table
    .getRowModel()
    .rows.filter((r) => enabledCheckboxes[r.index])
    .map((r) => r.original);
  return selectedEntities;
}

const getCommonPinningStyles = <T,>(
  column: Column<T>,
  isTableScrolledHorizontally: boolean
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    minWidth: `${column.columnDef.minSize}px`,
    width: column.getSize(),
    ...(isLastLeftPinnedColumn
      ? isTableScrolledHorizontally
        ? { borderRight: '3px solid var(--border-color) !important' }
        : { paddingRight: '18px !important' }
      : {}),
  };
};
